import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCardapio, getProdutoDetalhes, verifyToken } from '../services/api';
import { useCarrinho } from '../services/CarrinhoContext';
import ProdutoModal from '../components/ProdutoModal';
import { ArrowLeft, ShoppingBag, ShareAndroid, Heart, NavArrowDown, Menu } from 'iconoir-react';
import { formatPrice } from '../utils/currency';
import Loading from '../components/Loading';
import NavigationMenu from '../components/NavigationMenu';
import LoginModal from '../components/LoginModal';
import PerfilOffcanvas from '../components/PerfilOffcanvas';
import MeusPedidosOffcanvas from '../components/MeusPedidosOffcanvas';

const getVideoEmbedUrl = (url) => {
  if (!url) return null;
  
  const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([^&\n?#]+)/);
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1&mute=1&loop=1&playlist=${youtubeMatch[1]}&rel=0&modestbranding=1`;
  }
  
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&muted=1&loop=1&controls=1`;
  }
  
  if (url.match(/\.(mp4|webm|ogg)$/i)) {
    return url;
  }
  
  return null;
};

function Videos({ brand, delivery = false, balcao = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { adicionarItem } = useCarrinho();
  const [returnPath, setReturnPath] = useState('/');
  const [loja, setLoja] = useState(null);
  const [usuario, setUsuario] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showPerfil, setShowPerfil] = useState(false);
  const [showMeusPedidos, setShowMeusPedidos] = useState(false);

  useEffect(() => {
    // Detectar de onde o usuário veio baseado na rota atual
    const currentPath = location.pathname;
    
    if (currentPath.includes('/delivery/videos')) {
      setReturnPath('/delivery');
    } else if (currentPath.includes('/balcao/videos')) {
      setReturnPath('/balcao');
    } else if (currentPath.match(/^\/dinein\/[^/]+\/videos/)) {
      const token = currentPath.split('/dinein/')[1]?.split('/')[0];
      setReturnPath(token ? `/dinein/${token}` : '/');
    } else if (currentPath.match(/^\/tablet\/[^/]+\/videos/)) {
      const token = currentPath.split('/tablet/')[1]?.split('/')[0];
      setReturnPath(token ? `/tablet/${token}` : '/');
    } else {
      // Fallback: tentar usar state ou referrer
      const from = location.state?.from || '';
      if (from.includes('/delivery')) {
        setReturnPath('/delivery');
      } else if (from.includes('/balcao')) {
        setReturnPath('/balcao');
      } else {
        setReturnPath('/');
      }
    }
  }, [location]);
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showPlayer, setShowPlayer] = useState(false);
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const containerRef = useRef(null);
  const touchStartRef = useRef(0);
  const isSwipingRef = useRef(false);
  const currentIndexRef = useRef(0);

  useEffect(() => {
    carregarProdutosComVideo();
  }, [brand, delivery, balcao]);

  // Verificar token do usuário
  useEffect(() => {
    const verificarToken = async () => {
      const token = localStorage.getItem('sitri_token');
      if (token) {
        try {
          const resultado = await verifyToken(token);
          if (resultado && resultado.success && resultado.usuario) {
            setUsuario(resultado.usuario);
          }
        } catch (error) {
          // Token inválido
        }
      } else {
        // Tentar carregar do localStorage
        const usuarioSalvo = localStorage.getItem('sitri_user');
        if (usuarioSalvo) {
          try {
            setUsuario(JSON.parse(usuarioSalvo));
          } catch (error) {}
        }
      }
    };
    verificarToken();
  }, []);

  const carregarProdutosComVideo = async () => {
    try {
      setLoading(true);
      const data = await getCardapio(brand, delivery);
      
      if (data.loja) setLoja(data.loja);
      
      const produtosComVideo = [];
      data.categorias?.forEach(categoria => {
        categoria.produtos?.forEach(produto => {
          if (produto.video) {
            // Filtrar produtos baseado no contexto
            if (delivery && produto.b_delivery === false) {
              return; // Pular produtos desabilitados para delivery
            }
            if (balcao && produto.b_balcao === false) {
              return; // Pular produtos desabilitados para balcão
            }
            produtosComVideo.push(produto);
          }
        });
      });
      
      setProdutos(produtosComVideo);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    } finally {
      setLoading(false);
    }
  };

  const abrirPlayer = (index) => {
    setCurrentIndex(index);
    currentIndexRef.current = index;
    setShowPlayer(true);
  };

  const handleShare = async (produto) => {
    const url = `${window.location.origin}/produto/${produto.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: produto.nome,
          text: `Confira ${produto.nome}`,
          url: url
        });
      } catch (error) {
        // Usuário cancelou
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        alert('Link copiado!');
      } catch (error) {
        alert(`Link: ${url}`);
      }
    }
  };

  const abrirProduto = async (produto) => {
    try {
      const detalhes = await getProdutoDetalhes(brand, produto.id);
      setProdutoSelecionado(detalhes);
      setShowPlayer(false);
      setShowModal(true);
    } catch (error) {
      console.error('Erro ao abrir produto:', error);
    }
  };

  const handleOpenVideoPlayer = (produto) => {
    setShowModal(false);
    const index = produtos.findIndex(p => p.id === produto.id);
    if (index !== -1) {
      currentIndexRef.current = index;
      setCurrentIndex(index);
      setShowPlayer(true);
    }
  };

  const proximoVideo = () => {
    const newIndex = currentIndexRef.current < produtos.length - 1 ? currentIndexRef.current + 1 : 0;
    currentIndexRef.current = newIndex;
    setCurrentIndex(newIndex);
  };

  const videoAnterior = () => {
    if (currentIndexRef.current > 0) {
      const newIndex = currentIndexRef.current - 1;
      currentIndexRef.current = newIndex;
      setCurrentIndex(newIndex);
    }
  };

  useEffect(() => {
    if (!showPlayer) return;

    const handleTouchStart = (e) => {
      touchStartRef.current = e.touches[0].clientY;
      isSwipingRef.current = false;
    };

    const handleTouchMove = (e) => {
      const touchCurrent = e.touches[0].clientY;
      const diff = Math.abs(touchStartRef.current - touchCurrent);
      
      if (diff > 20) {
        isSwipingRef.current = true;
        e.preventDefault(); // Prevenir pull-to-refresh
      }
    };

    const handleTouchEnd = (e) => {
      if (!isSwipingRef.current) return;
      
      const touchEnd = e.changedTouches[0].clientY;
      const swipeDistance = touchStartRef.current - touchEnd;
      
      if (Math.abs(swipeDistance) > 50) {
        if (swipeDistance > 0) {
          const newIndex = currentIndexRef.current < produtos.length - 1 ? currentIndexRef.current + 1 : 0;
          currentIndexRef.current = newIndex;
          setCurrentIndex(newIndex);
        } else if (swipeDistance < 0 && currentIndexRef.current > 0) {
          const newIndex = currentIndexRef.current - 1;
          currentIndexRef.current = newIndex;
          setCurrentIndex(newIndex);
        }
      }
      
      isSwipingRef.current = false;
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [showPlayer, produtos.length]);

  const handleMenuClick = (action) => {
    switch(action) {
      case 'loja':
        navigate('/');
        break;
      case 'cardapio':
        if (delivery) {
          navigate('/delivery');
        } else if (balcao) {
          navigate('/balcao');
        } else if (location.pathname.match(/^\/dinein\/[^/]+/)) {
          const token = location.pathname.split('/dinein/')[1]?.split('/')[0];
          navigate(token ? `/dinein/${token}` : '/cardapio');
        } else if (location.pathname.match(/^\/tablet\/[^/]+/)) {
          const token = location.pathname.split('/tablet/')[1]?.split('/')[0];
          navigate(token ? `/tablet/${token}` : '/cardapio');
        } else {
          navigate('/cardapio');
        }
        break;
      case 'videos':
        break;
      case 'meusPedidos':
        setShowMeusPedidos(true);
        break;
      case 'perfil':
        if (usuario) {
          setShowPerfil(true);
        } else {
          setShowLogin(true);
        }
        break;
      default:
        break;
    }
  };

  const handleLogin = (userData) => {
    setUsuario(userData);
    localStorage.setItem('sitri_user', JSON.stringify(userData));
    setShowLogin(false);
  };

  const handleLogout = () => {
    setUsuario(null);
    localStorage.removeItem('sitri_user');
    localStorage.removeItem('sitri_token');
    setShowPerfil(false);
  };

  if (loading) {
    return <Loading message="Carregando vídeos..." />;
  }

  if (produtos.length === 0) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ textAlign: 'center' }}>
          <h3>Nenhum produto com vídeo encontrado</h3>
          <button onClick={() => navigate(returnPath)} style={{ marginTop: '20px', padding: '10px 20px', background: '#0039ff', color: '#fff', border: 'none', borderRadius: '8px' }}>
            Voltar ao Cardápio
          </button>
        </div>
      </div>
    );
  }

  if (showPlayer) {
    const produto = produtos[currentIndex];
    const videoUrl = getVideoEmbedUrl(produto.video);
    const isDirectVideo = videoUrl && videoUrl.match(/\.(mp4|webm|ogg)$/i);

    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#000', zIndex: 9999 }}>
        <button
          onClick={() => setShowPlayer(false)}
          style={{
            position: 'absolute',
            top: '16px',
            left: '16px',
            background: 'rgba(0, 0, 0, 0.6)',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            color: '#fff',
            fontSize: '20px',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <ArrowLeft />
        </button>

        <button
          onClick={() => navigate(returnPath)}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'rgba(0, 0, 0, 0.6)',
            border: 'none',
            borderRadius: '20px',
            padding: '8px 16px',
            color: '#fff',
            fontSize: '12px',
            fontWeight: '600',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}
        >
          <Menu width={18} height={18} />
          VER CARDÁPIO
        </button>

        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
          {isDirectVideo ? (
            <video
              key={currentIndex}
              src={videoUrl}
              controls={false}
              autoPlay
              muted
              playsInline
              loop
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <iframe
              key={currentIndex}
              src={videoUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
              allow="autoplay; encrypted-media; fullscreen"
              allowFullScreen
            />
          )}

          {/* Overlay com degradê */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '40%',
            background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
            pointerEvents: 'none'
          }} />

          {/* Barra lateral de ações (estilo TikTok) */}
          <div style={{
            position: 'absolute',
            right: 'clamp(8px, 2vw, 12px)',
            bottom: 'clamp(180px, 25vh, 220px)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'clamp(12px, 3vh, 20px)',
            alignItems: 'center',
            pointerEvents: 'auto',
            zIndex: 10001
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <button
                onClick={() => handleShare(produto)}
                style={{
                  background: 'rgba(255, 255, 255, 0.3)',
                  border: 'none',
                  borderRadius: '50%',
                  width: 'clamp(44px, 10vw, 48px)',
                  height: 'clamp(44px, 10vw, 48px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <ShareAndroid width={24} height={24} color="#fff" />
              </button>
              <span style={{ fontSize: 'clamp(9px, 2.5vw, 10px)', fontWeight: '600', color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>COMPARTILHAR</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <button
                onClick={proximoVideo}
                style={{
                  background: 'rgba(255, 255, 255, 0.3)',
                  border: 'none',
                  borderRadius: '50%',
                  width: 'clamp(44px, 10vw, 48px)',
                  height: 'clamp(44px, 10vw, 48px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <NavArrowDown width={24} height={24} color="#fff" />
              </button>
              <span style={{ fontSize: 'clamp(9px, 2.5vw, 10px)', fontWeight: '600', color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>PRÓXIMO</span>
            </div>
          </div>

          {/* Card do produto */}
          <div style={{
            position: 'absolute',
            bottom: 'clamp(16px, 3vh, 20px)',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80%',
            maxWidth: '500px',
            zIndex: 100
          }}>
            <div style={{
              background: 'rgba(0, 0, 0, 0.75)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              borderRadius: '20px',
              padding: 'clamp(14px, 3.5vh, 18px)',
              display: 'flex',
              gap: 'clamp(12px, 3vw, 14px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <img
                src={produto.imagem ? `https://grubupload.s3.amazonaws.com/${produto.imagem}` : 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg'}
                alt={produto.nome}
                style={{
                  width: 'clamp(70px, 18vw, 90px)',
                  height: 'clamp(70px, 18vw, 90px)',
                  borderRadius: '12px',
                  objectFit: 'cover',
                  flexShrink: 0
                }}
                onError={(e) => {
                  e.target.src = 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg';
                }}
              />
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ 
                  margin: '0 0 clamp(4px, 1vh, 6px) 0', 
                  fontSize: 'clamp(15px, 3.8vw, 17px)', 
                  fontWeight: '700',
                  color: '#fff',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textAlign: 'left'
                }}>
                  {produto.nome}
                </h3>
                <p style={{ 
                  margin: '0 0 clamp(8px, 2vh, 10px) 0', 
                  fontSize: 'clamp(12px, 2.8vw, 13px)', 
                  color: 'rgba(255, 255, 255, 0.7)',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.3,
                  textAlign: 'left'
                }}>
                  {produto.descricao}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', gap: '10px' }}>
                  <div style={{
                    fontSize: 'clamp(17px, 4.2vw, 19px)',
                    fontWeight: '700',
                    color: '#10b981',
                    textAlign: 'left'
                  }}>
                    {formatPrice(produto.valor)}
                  </div>
                  <button
                    onClick={() => abrirProduto(produto)}
                    style={{
                      background: '#007bff',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '10px',
                      padding: 'clamp(8px, 2vh, 10px) clamp(14px, 3.5vw, 16px)',
                      fontSize: 'clamp(11px, 2.8vw, 12px)',
                      fontWeight: '700',
                      cursor: 'pointer',
                      pointerEvents: 'auto',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 8px rgba(0, 123, 255, 0.3)'
                    }}
                  >
                    VER PRODUTO
                  </button>
                </div>
              </div>
            </div>
            <div style={{ 
              marginTop: 'clamp(8px, 2vh, 10px)', 
              fontSize: 'clamp(10px, 2.5vw, 11px)', 
              color: 'rgba(255, 255, 255, 0.6)',
              textAlign: 'center',
              fontWeight: '600'
            }}>
              {currentIndex + 1} de {produtos.length}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', paddingBottom: '80px' }}>
      <div style={{ position: 'sticky', top: 0, background: '#fff', padding: '12px 16px', borderBottom: '1px solid #e5e7eb', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={() => navigate(returnPath)} style={{ background: 'transparent', border: 'none', padding: '8px' }}>
            <ArrowLeft width={24} height={24} />
          </button>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>Clips curtos de produtos</h2>
        </div>
        <NavigationMenu
          onMenuClick={handleMenuClick}
          activeMenu="videos"
          hasHighlights={false}
          lojaLogoUrl={loja?.logo_url}
          loja={loja}
          hasVideos={true}
          usuario={usuario}
        />
      </div>

      <div style={{ 
        padding: '12px', 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(120px, 100%), 1fr))', 
        gap: '8px'
      }}>
        <style>{`
          @media (min-width: 480px) {
            .videos-grid {
              grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)) !important;
              gap: 12px !important;
              padding: 16px !important;
            }
          }
          @media (min-width: 768px) {
            .videos-grid {
              grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)) !important;
              gap: 16px !important;
            }
          }
          @media (min-width: 1024px) {
            .videos-grid {
              grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)) !important;
            }
          }
        `}</style>
        {produtos.map((produto, index) => (
          <div
            key={produto.id}
            onClick={() => abrirPlayer(index)}
            style={{
              position: 'relative',
              aspectRatio: '9/16',
              borderRadius: '12px',
              overflow: 'hidden',
              cursor: 'pointer',
              background: '#000'
            }}
          >
            <img
              src={produto.imagem ? `https://grubupload.s3.amazonaws.com/${produto.imagem}` : 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg'}
              alt={produto.nome}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => {
                e.target.src = 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg';
              }}
            />
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to bottom, transparent 50%, rgba(0,0,0,0.7))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '50%',
                width: 'clamp(40px, 15vw, 60px)',
                height: 'clamp(40px, 15vw, 60px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 'clamp(16px, 5vw, 24px)'
              }}>
                ▶
              </div>
            </div>
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              padding: 'clamp(8px, 2vw, 12px)',
              color: '#fff'
            }}>
              <h4 style={{ 
                margin: 0, 
                fontSize: 'clamp(11px, 3vw, 14px)', 
                fontWeight: '600',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>{produto.nome}</h4>
            </div>
          </div>
        ))}
      </div>

      {showModal && produtoSelecionado && (
        <ProdutoModal
          produto={produtoSelecionado}
          show={showModal}
          onHide={() => {
            setShowModal(false);
            setProdutoSelecionado(null);
            setShowPlayer(true);
          }}
          onOpenVideoPlayer={handleOpenVideoPlayer}
        />
      )}

      <LoginModal
        show={showLogin}
        onHide={() => setShowLogin(false)}
        onLogin={handleLogin}
        brandLogoUrl={loja?.logo_url}
      />

      {showPerfil && (
        <PerfilOffcanvas
          show={showPerfil}
          onHide={() => setShowPerfil(false)}
          usuario={usuario}
          onLogout={handleLogout}
        />
      )}

      {showMeusPedidos && (
        <MeusPedidosOffcanvas
          show={showMeusPedidos}
          onHide={() => setShowMeusPedidos(false)}
        />
      )}
    </div>
  );
}

export default Videos;
